import React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn, FOCUS_RING } from '@cotera/client/v0/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import {
  CollapsedCrumbMenu,
  CrumbSeparator,
  crumbDepthIndent,
} from '@cotera/client/v0/components/ui/breadcrumb-chrome';
import type { ArtifactDescriptor } from './artifacts';
import { ArtifactIcon } from './artifacts/artifact-type-icon';
import { useArtifactTitle } from './use-artifact-title';
import { artifactMatchesDirtyResource } from './artifact-dirty-state';
import { ArtifactDirtyDot } from './artifact-dirty-dot';
import { useDirtyArtifactResourceIds } from './use-dirty-artifact-resource-ids';
import { useArchivedArtifactIds } from './use-archived-artifact-ids';
import type { ArtifactPanelViewEntry } from './artifact-panel-context';
import { usePanelUnifiedChrome } from './artifact-panel-context';

/**
 * How many trailing drill-in crumbs stay on the bar. Everything before them
 * folds into the `…` menu, which always lists the *whole* path regardless of
 * what is on the bar — so a deep stack (Run › Step › Run › Step …) is never
 * unreachable, only unrendered.
 */
const VISIBLE_TAIL_VIEW_CRUMBS = 2;

/**
 * Renders one artifact's live title (its own hook, so it works in a list).
 *
 * Carries a native `title` because every place this renders can truncate — the
 * switcher floors at 6rem, the menu at 17rem — and a name clipped to a few
 * characters with no way to read the rest is worse than no name.
 */
const ArtifactTitleText: React.FC<{ artifact: ArtifactDescriptor }> = ({
  artifact,
}) => {
  const title = useArtifactTitle(artifact);
  return (
    <span className="truncate" title={title}>
      {title}
    </span>
  );
};

/**
 * Bumps a key every time `value` changes. Using it as a React `key` remounts
 * the node, which is what restarts a one-shot CSS animation. Starts at 0 so the
 * initial render doesn't flash.
 */
const useChangeFlashKey = (value: number): number => {
  const [flashKey, setFlashKey] = React.useState(0);
  const previous = React.useRef(value);
  React.useEffect(() => {
    if (previous.current !== value) {
      previous.current = value;
      setFlashKey((key) => key + 1);
    }
  }, [value]);
  return flashKey;
};

/** One row of the switcher menu: icon, live title, dirty dot, close. */
const ArtifactSwitcherRow: React.FC<{
  artifact: ArtifactDescriptor;
  isActive: boolean;
  isDirty: boolean;
  onSelect: (artifactId: string) => void;
}> = ({ artifact, isActive, isDirty, onSelect }) => (
  <DropdownMenuItem
    onSelect={() => onSelect(artifact.artifactId)}
    className="gap-2"
  >
    <Check
      className={cn(
        'h-3.5 w-3.5 shrink-0',
        isActive ? 'opacity-100' : 'opacity-0'
      )}
    />
    <ArtifactIcon
      artifactType={artifact.type}
      size="small"
      className="shrink-0 text-muted-foreground"
    />
    <span className="min-w-0 flex-1 truncate">
      <ArtifactTitleText artifact={artifact} />
    </span>
    {isDirty ? <ArtifactDirtyDot /> : null}
  </DropdownMenuItem>
);

/**
 * The bar's leading control: the focused artifact's own icon and title, which
 * *is* the switcher across everything open in the panel.
 *
 * It used to be a separate `Artifacts N ▾` crumb sitting between the chat title
 * and the artifact title. That spent ~90px naming a concept and another ~110px
 * repeating the chat title from the page bar above, which left the artifact
 * title — the one label that says what you are looking at — ellipsized to a few
 * characters in a half-width panel. Hanging the menu off the title itself buys
 * that space back and puts the affordance on the thing it acts on.
 *
 * The count badge appears only at two or more artifacts, and flashes when the
 * set changes so the switcher advertises that its contents moved.
 */
const ArtifactSwitcher: React.FC<{
  artifacts: readonly ArtifactDescriptor[];
  activeArtifactId: string | null;
  active: ArtifactDescriptor | null;
  selectArtifact: (artifactId: string) => void;
  /** True when the trail is showing, which demotes the title to a link. */
  drilledIn: boolean;
}> = ({ artifacts, activeArtifactId, active, selectArtifact, drilledIn }) => {
  const dirtyResourceIds = useDirtyArtifactResourceIds();
  const archivedArtifactIds = useArchivedArtifactIds();

  /**
   * What the menu offers to switch to: everything still live, plus whatever is
   * open right now even if that has been archived.
   *
   * The exception is the point. Archiving from the artifact's own `⋯` menu
   * happens with the artifact on screen, and dropping it from its own switcher
   * on the same click leaves the bar naming a tab whose menu says it does not
   * exist — this is the one row that is not a destination but a statement of
   * where you are. It stays until the user leaves it, and its body is the
   * archived notice rather than an editor.
   *
   * Everything counted in the bar reads this rather than `artifacts`, so the
   * badge, the aria-label and the rows under them cannot disagree.
   */
  const switchable = React.useMemo(
    () =>
      archivedArtifactIds.size === 0
        ? artifacts
        : artifacts.filter(
            (artifact) =>
              !archivedArtifactIds.has(artifact.artifactId) ||
              artifact.artifactId === activeArtifactId
          ),
    [artifacts, archivedArtifactIds, activeArtifactId]
  );

  const flashKey = useChangeFlashKey(switchable.length);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'group inline-flex min-w-0 shrink items-center gap-1.5 rounded px-1.5 py-1 text-sm transition-colors hover:bg-muted',
          FOCUS_RING,
          // Floor only. Below it the title still shows ~8 characters; the trail
          // gives up its crumbs before this does.
          'min-w-[6rem] cursor-pointer',
          // Capped, but generously. 15rem truncated names to "[Demo] Final
          // Archiv…" with half the bar sitting empty; no cap at all let one
          // long title run the width of the pane. The title gets room without
          // becoming the only thing in the bar.
          //
          // Drilled in it yields, since the path now needs the space. Both caps
          // lift once the bar itself is wide — the expanded layout — rather
          // than at some viewport size that says nothing about this panel.
          drilledIn === true
            ? 'max-w-[15rem] @min-[60rem]/panel-bar:max-w-[26rem]'
            : 'max-w-[20rem] @min-[60rem]/panel-bar:max-w-[32rem]',
          drilledIn
            ? 'text-muted-foreground hover:text-foreground'
            : 'font-medium text-foreground'
        )}
        aria-label={
          switchable.length > 1
            ? `Switch artifact (${switchable.length} open)`
            : 'Artifact menu'
        }
      >
        {active !== null ? (
          <ArtifactIcon
            artifactType={active.type}
            size="small"
            className="shrink-0 text-muted-foreground"
          />
        ) : null}
        <span className="min-w-0 flex-1 truncate text-left">
          {active !== null ? (
            <ArtifactTitleText artifact={active} />
          ) : (
            'Artifact'
          )}
        </span>
        {switchable.length > 1 ? (
          <span
            key={flashKey}
            className={cn(
              'shrink-0 rounded bg-muted px-1 text-[10px] font-bold leading-4 text-muted-foreground',
              flashKey > 0 && 'motion-safe:animate-count-flash'
            )}
          >
            {switchable.length}
          </span>
        ) : null}
        <ChevronDown className="h-3 w-3 shrink-0 stroke-[2.5] text-muted-foreground/70" />
      </DropdownMenuTrigger>
      {/* Sizes to the longest name rather than a fixed width, so the switcher
          is a place you can actually read the titles, bounded so one long name
          can't stretch it across the pane. */}
      <DropdownMenuContent
        align="start"
        className="max-h-[20rem] w-max min-w-[17rem] max-w-[26rem] overflow-y-auto"
      >
        {switchable.map((artifact) => (
          <ArtifactSwitcherRow
            key={artifact.artifactId}
            artifact={artifact}
            isActive={artifact.artifactId === activeArtifactId}
            isDirty={artifactMatchesDirtyResource(artifact, dirtyResourceIds)}
            onSelect={selectArtifact}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * The `…` control standing in for drill-in levels that don't fit on the bar.
 *
 * Its menu is the complete path, not just what was folded away, so it stays
 * correct however many crumbs the container query decided to paint — and at any
 * depth it is a single click to any ancestor, including the artifact root.
 */
const CollapsedViewCrumbs: React.FC<{
  activeTitle: React.ReactNode;
  entries: readonly ArtifactPanelViewEntry[];
  popToViewDepth: (depth: number) => void;
}> = ({ activeTitle, entries, popToViewDepth }) => (
  <CollapsedCrumbMenu
    label={`Show ${entries.length + 1} levels above`}
    contentClassName="w-[17rem]"
  >
    <DropdownMenuItem onSelect={() => popToViewDepth(0)} className="truncate">
      {activeTitle}
    </DropdownMenuItem>
    {entries.map((entry, i) => (
      <DropdownMenuItem
        key={entry.id}
        onSelect={() => popToViewDepth(i + 1)}
        className="truncate"
        style={crumbDepthIndent(i + 1)}
      >
        {entry.title ?? 'View'}
      </DropdownMenuItem>
    ))}
  </CollapsedCrumbMenu>
);

const CRUMB_LINK = cn(
  'cursor-pointer truncate rounded px-1 py-0.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
  FOCUS_RING
);
const CRUMB_CURRENT = 'truncate px-1 text-sm font-medium text-foreground';

/**
 * The single unified trail shown in the artifact panel:
 *
 *   [icon] <artifact title> ▾ⁿ  ›  … ›  <pushView stack…>
 *
 * The leading control is the artifact switcher ({@link ArtifactSwitcher}); the
 * crumbs after it are the focused artifact's drill-in (`pushView`) stack, and
 * clicking one pops back to it. Rendered by {@link ResourcePageShell} in place
 * of a resource's own breadcrumbs whenever a {@link PanelUnifiedChrome} is in
 * scope (desktop artifact panel only).
 *
 * Space is allocated worst-crumb-first. The switcher holds a floor of 6rem, the
 * last crumb is never dropped, and the levels between them give way in the
 * middle — first to a container query as the panel narrows, then to the
 * {@link VISIBLE_TAIL_VIEW_CRUMBS} cap when the stack is simply deep. Whatever
 * leaves the bar is still one click away in the `…` menu, which always carries
 * the whole path.
 */
export const UnifiedPanelBreadcrumbs: React.FC<{
  /**
   * Rendered as the trail's last element, inside this nav.
   *
   * For a control that belongs to the artifact being named rather than to the
   * panel — the favorites star. It has to live *in* here: this nav is the
   * `flex-1` element in the bar, so a sibling placed after it is pushed to the
   * far right and lands among the panel's own controls instead of beside the
   * artifact it marks.
   */
  trailing?: React.ReactNode;
}> = ({ trailing }) => {
  const chrome = usePanelUnifiedChrome();
  if (chrome === null) {
    return null;
  }
  const {
    artifacts,
    activeArtifactId,
    selectArtifact,
    viewStack,
    popToViewDepth,
  } = chrome;

  // Picking the artifact you are already in means "take me to the top of it" —
  // otherwise that row is the one dead entry in the menu, and at depth 1 (where
  // the `…` menu hasn't appeared yet) it is a second way back to the root.
  const onSelectArtifact = (artifactId: string): void => {
    if (artifactId === activeArtifactId) {
      popToViewDepth(0);
      return;
    }
    selectArtifact(artifactId);
  };

  const active =
    artifacts.find((a) => a.artifactId === activeArtifactId) ?? null;
  const activeTitle =
    active !== null ? <ArtifactTitleText artifact={active} /> : 'Artifact';

  // Levels the cap folds away outright. Anything still on the bar can be hidden
  // again by the container query below, but the `…` menu covers both.
  const collapsedCount = Math.max(
    0,
    viewStack.length - VISIBLE_TAIL_VIEW_CRUMBS
  );
  const tail = viewStack.slice(collapsedCount);
  const hasHiddenLevels = viewStack.length > 1;

  return (
    <nav
      className="flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden"
      aria-label="Breadcrumb"
    >
      <ArtifactSwitcher
        artifacts={artifacts}
        activeArtifactId={activeArtifactId}
        active={active}
        selectArtifact={onSelectArtifact}
        drilledIn={viewStack.length > 0}
      />
      {hasHiddenLevels ? (
        <>
          <CrumbSeparator />
          <CollapsedViewCrumbs
            activeTitle={activeTitle}
            entries={viewStack.slice(0, viewStack.length - 1)}
            popToViewDepth={popToViewDepth}
          />
        </>
      ) : null}
      {tail.map((entry, i) => {
        const depth = collapsedCount + i + 1;
        const isLast = depth === viewStack.length;
        const label = entry.title ?? 'View';
        return (
          <React.Fragment key={entry.id}>
            {/* Intermediate crumbs are the first thing to go as the panel
                narrows — the `…` menu beside them already holds the full path,
                so nothing becomes unreachable, only unprinted. */}
            <CrumbSeparator
              className={
                isLast ? undefined : 'hidden @min-[34rem]/panel-bar:block'
              }
            />
            {isLast ? (
              <span
                className={cn(CRUMB_CURRENT, 'min-w-0 max-w-[16rem]')}
                title={label}
              >
                {label}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => popToViewDepth(depth)}
                title={label}
                className={cn(
                  CRUMB_LINK,
                  'hidden min-w-0 max-w-[10rem] @min-[34rem]/panel-bar:inline-block'
                )}
              >
                {label}
              </button>
            )}
          </React.Fragment>
        );
      })}
      {trailing}
    </nav>
  );
};
